import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Breadcrumbs from '../../components/Breadcrumbs';

const PageContainer = styled.div`
  padding-top: 80px;
`;

const Hero = styled.section`
  position: relative;
  padding: 8rem 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  overflow: hidden;
  text-align: center;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(37, 99, 235, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(37, 99, 235, 0.08) 0%, transparent 50%),
      url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232563eb' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
`;

const HeroText = styled.div`
  position: relative;
  z-index: 1;
`;

const Badge = styled(motion.span)`
  display: inline-block;
  padding: 0.5rem 1rem;
  background: rgba(37, 99, 235, 0.1);
  color: #2563eb;
  border-radius: 30px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #64748b;
  margin-bottom: 2rem;
  line-height: 1.7;
`;

const HeroImage = styled(motion.div)`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 100%;
    height: 100%;
    filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.1));
  }
`;

const StatsSection = styled.section`
  padding: 4rem 2rem;
  background: white;
`;

const StatsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled(motion.div)`
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(37, 99, 235, 0.1);
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: #2563eb;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: #64748b;
  font-size: 1rem;
  font-weight: 500;
`;

const FeaturesSection = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
`;

const FeatureGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(37, 99, 235, 0.1);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureIcon = styled.div`
  width: 48px;
  height: 48px;
  background: rgba(37, 99, 235, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const FeatureDescription = styled.p`
  color: #64748b;
  line-height: 1.6;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: #1e293b;
  text-align: center;
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  color: #64748b;
  text-align: center;
  max-width: 600px;
  margin: 0 auto 4rem;
  line-height: 1.6;
`;

const TestimonialsSection = styled.section`
  padding: 6rem 2rem;
  background: white;
`;

const TestimonialGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(37, 99, 235, 0.1);
`;

const TestimonialText = styled.p`
  font-size: 1.1rem;
  color: #1e293b;
  margin-bottom: 1.5rem;
  line-height: 1.7;
  font-style: italic;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AuthorAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #2563eb;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
`;

const AuthorInfo = styled.div``;

const AuthorName = styled.div`
  font-weight: 600;
  color: #1e293b;
`;

const AuthorRole = styled.div`
  color: #64748b;
  font-size: 0.9rem;
`;

const CTASection = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
  color: white;
  text-align: center;
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
`;

const CTAText = styled.p`
  font-size: 1.25rem;
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const CTAButton = styled(motion.button)`
  background: white;
  color: #2563eb;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(0, 0, 0, 0.2);
  }
`;

const ProcessSection = styled.section`
  padding: 6rem 2rem;
  background: white;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232563eb' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const ProcessTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 1rem;
  color: #1e293b;
`;

const ProcessSubtitle = styled.p`
  text-align: center;
  color: #64748b;
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto 4rem;
  line-height: 1.6;
`;

const ProcessGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    top: 100px;
    left: 50px;
    right: 50px;
    height: 2px;
    background: linear-gradient(90deg, 
      rgba(37, 99, 235, 0.1) 0%, 
      rgba(37, 99, 235, 0.7) 20%, 
      rgba(37, 99, 235, 0.7) 80%, 
      rgba(37, 99, 235, 0.1) 100%
    );
    z-index: 0;
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    &:before {
      display: none;
    }
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ProcessCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(37, 99, 235, 0.1);
  position: relative;
  z-index: 1;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.1);
  }
`;

const ProcessStep = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(37, 99, 235, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%);
    z-index: -1;
  }
`;

const ProcessCardTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const ProcessDescription = styled.p`
  color: #64748b;
  line-height: 1.7;
`;

const BenefitsSection = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232563eb' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const BenefitsTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 1rem;
  color: #1e293b;
`;

const BenefitsSubtitle = styled.p`
  text-align: center;
  color: #64748b;
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto 4rem;
  line-height: 1.6;
`;

const BenefitsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(37, 99, 235, 0.1);
  transition: transform 0.3s ease;
  text-align: left;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.1);
  }
`;

const BenefitIcon = styled.div`
  width: 48px;
  height: 48px;
  background: rgba(37, 99, 235, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const BenefitContent = styled.div``;

const BenefitTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const BenefitDescription = styled.p`
  color: #64748b;
  line-height: 1.6;
`;

function CheckoutTesting() {
  return (
    <PageContainer>
      <Breadcrumbs items={[
        { label: 'Services', path: '/services' },
        { label: 'Checkout Testing', path: '/services/checkout' }
      ]} />
      <Hero>
        <HeroContent>
          <Badge
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            Checkout Testing
          </Badge>
          <Title
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Never Lose Another Sale to Checkout Issues
          </Title>
          <Subtitle>
            Ensure your checkout process works flawlessly with our comprehensive testing solution. 
            Catch issues before they impact your customers and revenue.
          </Subtitle>
          <CTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Start Testing Now
          </CTAButton>
        </HeroContent>
      </Hero>

      <StatsSection>
        <StatsGrid>
          {[
            { number: '99.9%', label: 'Checkout Success Rate' },
            { number: '50%', label: 'Fewer Support Tickets' },
            { number: '2x', label: 'Faster Issue Resolution' },
            { number: '24/7', label: 'Monitoring & Alerts' }
          ].map((stat, index) => (
            <StatCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <StatNumber>{stat.number}</StatNumber>
              <StatLabel>{stat.label}</StatLabel>
            </StatCard>
          ))}
        </StatsGrid>
      </StatsSection>

      <ProcessSection>
        <ProcessTitle>How It Works</ProcessTitle>
        <ProcessSubtitle>
          Our comprehensive testing process ensures your checkout flow works flawlessly,
          from initial analysis to continuous monitoring.
        </ProcessSubtitle>
        <ProcessGrid>
          {[
            {
              step: '1',
              title: 'Initial Scan',
              description: 'We analyze your current checkout process and identify potential issues and vulnerabilities.'
            },
            {
              step: '2',
              title: 'Test Scenarios',
              description: 'Custom test cases are created based on your specific checkout flow and business requirements.'
            },
            {
              step: '3',
              title: 'Automated Testing',
              description: 'Continuous testing across multiple payment methods, browsers, and user scenarios.'
            },
            {
              step: '4',
              title: 'Real-time Monitoring',
              description: 'Proactive alerts and detailed reporting to maintain optimal checkout performance.'
            }
          ].map((process, index) => (
            <ProcessCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <ProcessStep>{process.step}</ProcessStep>
              <ProcessCardTitle>{process.title}</ProcessCardTitle>
              <ProcessDescription>{process.description}</ProcessDescription>
            </ProcessCard>
          ))}
        </ProcessGrid>
      </ProcessSection>

      <BenefitsSection>
        <BenefitsTitle>Why Choose Our Checkout Testing</BenefitsTitle>
        <BenefitsSubtitle>
          Protect your revenue with comprehensive testing that catches issues before they impact your customers
        </BenefitsSubtitle>
        <BenefitsGrid>
          {[
            {
              icon: '💰',
              title: 'Revenue Protection',
              description: 'Prevent lost sales with proactive issue detection and instant alerts'
            },
            {
              icon: '⚡',
              title: 'Lightning Fast Fixes',
              description: 'Identify and resolve issues in minutes, not hours'
            },
            {
              icon: '🔄',
              title: '24/7 Monitoring',
              description: 'Continuous testing ensures your checkout never fails'
            },
            {
              icon: '🌐',
              title: 'Universal Coverage',
              description: 'Test across all devices, browsers, and payment methods'
            },
            {
              icon: '🔒',
              title: 'Secure Transactions',
              description: 'Ensure payment data is processed securely and reliably'
            },
            {
              icon: '📊',
              title: 'Real-time Analytics',
              description: 'Monitor performance metrics and conversion rates'
            },
            {
              icon: '🔍',
              title: 'Error Prevention',
              description: 'Catch and fix issues before they affect customers'
            },
            {
              icon: '🔄',
              title: 'Automated Testing',
              description: 'Continuous validation of checkout functionality'
            },
            {
              icon: '📱',
              title: 'Mobile Optimization',
              description: 'Perfect checkout experience on all devices'
            },
            {
              icon: '⚙️',
              title: 'Custom Workflows',
              description: 'Tailored testing for your specific needs'
            }
          ].map((benefit, index) => (
            <BenefitCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <BenefitIcon>{benefit.icon}</BenefitIcon>
              <BenefitTitle>{benefit.title}</BenefitTitle>
              <BenefitDescription>{benefit.description}</BenefitDescription>
            </BenefitCard>
          ))}
        </BenefitsGrid>
      </BenefitsSection>

      <TestimonialsSection>
        <TestimonialGrid>
          {[
            {
              text: "Since implementing Revguard's checkout testing, we've seen a 40% reduction in abandoned carts and significantly improved our conversion rate.",
              author: "Sarah Johnson",
              role: "E-commerce Director at FashionCo"
            },
            {
              text: "The automated testing has saved our team countless hours and helped us maintain 99.9% uptime during peak sales events.",
              author: "Michael Chen",
              role: "CTO at GearHub"
            }
          ].map((testimonial, index) => (
            <TestimonialCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <TestimonialText>{testimonial.text}</TestimonialText>
              <TestimonialAuthor>
                <AuthorAvatar>
                  {testimonial.author.split(' ').map(n => n[0]).join('')}
                </AuthorAvatar>
                <AuthorInfo>
                  <AuthorName>{testimonial.author}</AuthorName>
                  <AuthorRole>{testimonial.role}</AuthorRole>
                </AuthorInfo>
              </TestimonialAuthor>
            </TestimonialCard>
          ))}
        </TestimonialGrid>
      </TestimonialsSection>

      <FeaturesSection>
        <SectionTitle>Comprehensive Testing Features</SectionTitle>
        <SectionSubtitle>Everything you need to ensure a flawless checkout experience</SectionSubtitle>
        <FeatureGrid>
          {[
            {
              icon: '🔒',
              title: 'Payment Gateway Testing',
              description: 'Test all payment methods and ensure smooth transactions across different gateways.'
            },
            {
              icon: '🛒',
              title: 'Cart Functionality',
              description: 'Verify cart calculations, discounts, and shipping options work correctly.'
            },
            {
              icon: '📱',
              title: 'Cross-Device Testing',
              description: 'Ensure consistent checkout experience across all devices and browsers.'
            },
            {
              icon: '⚡',
              title: 'Performance Testing',
              description: 'Monitor and optimize checkout speed and server response times.'
            },
            {
              icon: '🔍',
              title: 'Error Handling',
              description: 'Test error scenarios and ensure clear user feedback.'
            },
            {
              icon: '📊',
              title: 'Analytics & Reporting',
              description: 'Get detailed insights into checkout performance and issues.'
            }
          ].map((feature, index) => (
            <FeatureCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeatureGrid>
      </FeaturesSection>

      <CTASection>
        <CTAContent>
          <CTATitle>Ready to Optimize Your Checkout?</CTATitle>
          <CTAText>
            Get started with our comprehensive checkout testing solution and never lose another sale to technical issues.
          </CTAText>
          <CTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Start Free Trial
          </CTAButton>
        </CTAContent>
      </CTASection>
    </PageContainer>
  );
}

export default CheckoutTesting;